import { DEFAULT_CURRENCY } from '@constants/global';
import {
  CurrencyDisplay,
  CurrencyFormat,
  DEFAULT_CURRENCY_DISPLAY,
  DEFAULT_CURRENCY_FORMAT,
  LANG_TO_LOCALE,
} from '@domain/currency-preferences';

export const numberFormatCache = new Map<string, Intl.NumberFormat>();

export interface FormatOptions {
  number: number;
  currency?: string;
  shortened?: boolean;
  maximumFractionDigits?: number;
  minimumFractionDigits?: number;
  removeSpaces?: boolean;
  showPlusPrefix?: boolean;
  showNegativePrefix?: boolean;
}

let currentFormat: CurrencyFormat = DEFAULT_CURRENCY_FORMAT;
let currentDisplay: CurrencyDisplay = DEFAULT_CURRENCY_DISPLAY;
let currentLang = 'en';

export const setCurrencyPreferences = (format?: CurrencyFormat, display?: CurrencyDisplay, lang?: string): void => {
  currentFormat = format ?? DEFAULT_CURRENCY_FORMAT;
  currentDisplay = display ?? DEFAULT_CURRENCY_DISPLAY;
  currentLang = lang ?? 'en';
  numberFormatCache.clear();
};

const getLocale = (): string => {
  if (currentFormat === 'locale') {
    return LANG_TO_LOCALE[currentLang] ?? 'en-US';
  }
  return 'en-US';
};

const getCurrencyDisplayOption = (): 'narrowSymbol' | 'code' => {
  return currentDisplay === 'code' ? 'code' : 'narrowSymbol';
};

const createCacheKey = (
  currency: string,
  minFractionDigits: number,
  maxFractionDigits: number,
  shortened: boolean
): string => {
  return `${currentFormat}-${currentDisplay}-${currentLang}-${currency}-${minFractionDigits}-${maxFractionDigits}-${shortened}`;
};

export const formatCurrency = (options: FormatOptions): string => {
  const currency = options.currency ?? DEFAULT_CURRENCY;

  const minFractionDigits = options.minimumFractionDigits === 0 ? 0 : options.minimumFractionDigits ?? 2;
  const maxFractionDigits =
    options.maximumFractionDigits === undefined
      ? Math.max(2, minFractionDigits)
      : Math.max(options.maximumFractionDigits, minFractionDigits);

  const cacheKey = createCacheKey(currency, minFractionDigits, maxFractionDigits, options.shortened ?? false);

  let formatter = numberFormatCache.get(cacheKey);
  if (!formatter) {
    formatter = new Intl.NumberFormat(getLocale(), {
      style: 'currency',
      currency,
      currencyDisplay: getCurrencyDisplayOption(),
      minimumFractionDigits: minFractionDigits,
      maximumFractionDigits: maxFractionDigits,
      notation: options.shortened ? 'compact' : 'standard',
    });
    numberFormatCache.set(cacheKey, formatter);
  }

  let result: string;

  // By default we preserve the non-breaking spaces that Intl inserts per CLDR
  // rules (NBSP before/after symbol for European locales, always NBSP around
  // ISO codes). These non-breaking spaces are what makes `EUR 1,234.56` legible
  // vs the illegible `EUR1,234.56`.
  //
  // Pass `removeSpaces: true` as an escape hatch for very tight UI where the
  // symbol must be glued to the number.
  if (options.removeSpaces === true) {
    const parts = formatter.formatToParts(options.number);
    result = parts
      .filter((p) => p.type !== 'literal')
      .map((p) => p.value)
      .join('');
  } else {
    result = formatter.format(options.number);
  }

  // Add plus prefix for positive numbers if requested
  if (options.showPlusPrefix && options.number > 0) {
    result = `+${result}`;
  }

  if (options.showNegativePrefix && options.number >= 0) {
    result = `-${result}`;
  }

  return result;
};

/**
 * Formats a plain number (no currency) following the current user's currency
 * preferences so grouping/decimal separators stay consistent with
 * `formatCurrency` (e.g., both `1.234,56 €` and `1.234,56` in locale mode).
 */
export const formatValue = (options: FormatOptions): string => {
  const minFractionDigits = options.minimumFractionDigits ?? 0;
  const maxFractionDigits =
    options.maximumFractionDigits === undefined
      ? Math.max(0, minFractionDigits)
      : Math.max(options.maximumFractionDigits, minFractionDigits);

  const locale = getLocale();
  const key = `decimal-${locale}-${minFractionDigits}-${maxFractionDigits}-${options.shortened ?? false}`;

  let formatter = numberFormatCache.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, {
      style: 'decimal',
      useGrouping: true,
      minimumFractionDigits: minFractionDigits,
      maximumFractionDigits: maxFractionDigits,
      notation: options.shortened ? 'compact' : 'standard',
    });
    numberFormatCache.set(key, formatter);
  }

  return formatter.format(options.number);
};
